import { Card, Flex, Input } from 'antd';
import { CustomTableHeader } from '../custom-table-header/custom-table-header';
import styles from './documents-header.module.scss';
import { useMemo, useState } from 'react';
import Search from '@/assets/icons/search-md.svg?react';
import PlusIcon from '@/assets/icons/plus.svg?react';
import { debounce } from '@/utils/debounce/debounce';
import Dropdown from '../form/dropdown/dropdown';
import { useUser } from '@/hooks/use-user/use-user';
import { DocumentsFilters } from '@/services/documents-service/documents-service.types';
import { TYPE_OPTIONS } from './documents-header.constants';
import { CustomFilters } from '../custom-filters/custom-filters';
import { Button } from '../button/button';
import { useNavigate } from 'react-router';
import Icon from '@ant-design/icons';

interface DocumentsHeaderProps {
  companiesList: string[];
  documentsNumber: number;
  filters?: DocumentsFilters;
  onChangeFilters: (filters: DocumentsFilters) => void;
  onSearchQueryChange: (query: string) => void;
  searchResults?: string[];
  isSearchLoading?: boolean;
  searchError?: Error | null;
  isCompanySelectDisabled?: boolean;
}

export function DocumentsHeader({
  filters,
  onChangeFilters,
  companiesList,
  documentsNumber,
  onSearchQueryChange,
  searchResults,
  isSearchLoading,
  searchError,
  isCompanySelectDisabled,
}: DocumentsHeaderProps) {
  const [filterValue, setFilterValue] = useState(filters?.query || '');
  const { isFetched } = useUser();
  const navigate = useNavigate();

  const debouncedFilterChange = useMemo(() => debounce(onChangeFilters, 500), [onChangeFilters]);

  const handleQueryChange = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
    setFilterValue(value);
    debouncedFilterChange({ ...filters, query: value });
  };

  const handleTypeChange = (value: string) => {
    onChangeFilters({ ...filters, type: value });
  };

  return (
    <Card className={styles.container} style={{ padding: '16px 24px' }}>
      <Flex vertical className={styles.topHeaderContainer} gap={20}>
        <Flex gap={20} className={styles.documentsCountSectionWrapper}>
          <Flex gap={30} align="center" className={styles.documentsCountSectionContainer}>
            <CustomTableHeader
              textHeader1="Number of"
              textHeader2="documents"
              count={documentsNumber}
            />
          </Flex>
          <Flex gap={20} className={styles.filtersSectionContainer} align="center">
            <Dropdown
              className={styles.dropdown}
              wrapperClassName={styles.wrapperClassName}
              dropdownListClass={styles.typeDropdownList}
              onChange={handleTypeChange}
              placeholder="Type"
              options={TYPE_OPTIONS}
              value={filters?.type}
            />

            {isFetched && (
              <CustomFilters
                customClassName={styles.customSelectClassName}
                companiesList={companiesList}
                filters={filters || {}}
                defaultCompanyValue={undefined}
                onChangeFilters={(updatedFilters) => {
                  onChangeFilters({
                    ...filters,
                    company_name: updatedFilters.company_name,
                  });
                }}
                onSearchQueryChange={onSearchQueryChange}
                searchResults={searchResults}
                isSearchLoading={isSearchLoading}
                searchError={searchError}
                disabled={isCompanySelectDisabled}
              />
            )}
          </Flex>
        </Flex>
      </Flex>

      <Flex
        gap={20}
        className={styles.bottomHeaderContainer}
        wrap="wrap"
        justify="space-between"
        align="center"
      >
        <Flex>
          <Button buttonVariant="primary" onClick={() => navigate('/documents/new')}>
            <Icon component={() => <PlusIcon className={styles.plusIcon} />} />
            New document
          </Button>
        </Flex>
        <Flex vertical gap={5} className={styles.searchContainer}>
          <Input
            onChange={(event) => handleQueryChange(event)}
            value={filterValue}
            placeholder="Search"
            prefix={<Search stroke={styles.iconColor} height={16} width={16} strokeWidth={2.5} />}
          />
        </Flex>
      </Flex>
    </Card>
  );
}
